import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import Drawer from '@material-ui/core/Drawer';
import classnames from 'classnames';

import ORTypography from 'components/ORTypography/ORTypography';
import Price from 'components/Price/PriceContainer';

const propTypes = {
  availablePlans: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  availableAddons: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string })).isRequired,
  selectedService: PropTypes.string.isRequired,
  isServiceBeta: PropTypes.bool.isRequired,
  selectedVersion: PropTypes.string.isRequired,
  selectedCloudProvider: PropTypes.string,
  selectedCloudProviderIsBeta: PropTypes.bool.isRequired,
  selectedRegion: PropTypes.string,
  selectedFlavor: PropTypes.shape({ name: PropTypes.string }).isRequired,
  selectedAddons: PropTypes.arrayOf(PropTypes.shape({})),
  selectedPlan: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    nodes: PropTypes.shape({}),
  }).isRequired,
  selectedCidr: PropTypes.string.isRequired,
  enteredName: PropTypes.string,
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  getPrice: PropTypes.func.isRequired,
  price: PropTypes.number.isRequired,
  supportLevelName: PropTypes.string,
  regionsCatalog: PropTypes.shape({
    regions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  }).isRequired,
  planInfoString: PropTypes.string.isRequired,
  nodeInfoString: PropTypes.string.isRequired,
  isFastlaneCustomer: PropTypes.bool.isRequired,
};

const defaultProps = {
  selectedCloudProvider: '',
  selectedRegion: '',
  selectedAddons: [],
  enteredName: '',
  supportLevelName: '',
};

const useStyles = makeStyles((theme) => ({
  header: {
    borderBottom: `1px solid ${theme.customColors.lightGrey}`,
    color: theme.customColors.teal,
    textTransform: 'uppercase',
    padding: `0 ${theme.spacing(0.5)}px`,
    fontSize: '1.2857rem',
    display: 'grid',
    gridTemplateAreas: '"a a"',
    gridAutoColumns: 'min-content 1fr',
    cursor: 'pointer',
  },
  theX: {
    justifySelf: 'left',
    fontSize: '2.2857rem',
  },
  monthlyCostEstimate: {
    justifySelf: 'center',

    [theme.breakpoints.down('md')]: {
      justifySelf: 'left',
      marginLeft: theme.spacing(1),
    },
  },
  drawerPaper: {
    position: 'absolute',
    top: theme.spacing(1),
    minWidth: 330,
    backgroundColor: theme.palette.background.default,
    textAlign: 'left',

    [theme.breakpoints.up('lg')]: {
      textAlign: 'center',
    },

    [theme.breakpoints.down('md')]: {
      width: '100%',
      minWidth: 'unset',
    },

    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  content: {
    backgroundColor: theme.customColors.white,
    padding: theme.spacing(0.5),
    height: '100%',
  },
  content_header: {
    fontSize: '.8571rem',
    fontWeight: 'bold',
  },
  content_detail: {
    fontSize: '.8571rem',
  },
  content_beta_warning: {
    color: theme.palette.primary.main,
    maxWidth: '19rem',
    margin: '0 auto',

    [theme.breakpoints.down('md')]: {
      margin: '0 1rem 0 0',
      maxWidth: 'unset',
    },
  },
  content_beta_warning_link: {
    color: 'blue',
  },
  promotionalText: {
    display: 'flex',
    flexFlow: 'row nowrap',
    justifyContent: 'flex-start',
    alignItems: 'center',
    color: theme.customColors.mediumGrey,

    [theme.breakpoints.up('lg')]: {
      justifyContent: 'center',
    },
  },
  billingContent: {
    marginTop: '40px',
  },
  priceContainer: {
    display: 'flex',
    flexFlow: 'row nowrap',
    justifyContent: 'flex-start',
    alignItems: 'center',

    [theme.breakpoints.up('lg')]: {
      justifyContent: 'center',
    },
  },
  priceText: {
    color: theme.customColors.teal,
  },
  additionalPriceText: {
    color: theme.customColors.lightGrey,
    fontWeight: 500,
    fontSize: 21,
  },
  removePromo: {
    color: theme.customColors.magenta,
    backgroundColor: 'inherit',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: 'inherit',
    },
  },
  infoIcon: {
    marginLeft: theme.spacing(0.5),
  },
  supportText: {
    fontStyle: 'italic',
    fontSize: '.8571rem',
  },
  betaText: {
    color: theme.palette.primary.main,
    fontStyle: 'italic',
    fontSize: '0.7857142857rem',
    fontWeight: 'bold',
  },
}));

export function UnstyledSummaryDrawer({
  selectedService,
  isServiceBeta,
  selectedVersion,
  selectedCloudProvider,
  selectedCloudProviderIsBeta,
  selectedRegion,
  selectedFlavor,
  selectedAddons,
  selectedPlan,
  selectedCidr,
  enteredName,
  isOpen,
  toggle,
  getPrice,
  price,
  availablePlans,
  availableAddons,
  regionsCatalog,
  supportLevelName,
  planInfoString,
  nodeInfoString,
  isFastlaneCustomer,
}) {
  const { regions } = regionsCatalog;
  const { name: flavorName } = selectedFlavor;
  const classes = useStyles();

  const discount = supportLevelName === 'Promotional_20_Percent' ? 20 : 0;
  const discountCalculation = ((price - price * (1 - discount / 100)) / 100).toFixed(2);

  const promotionalTextBuilder = (primaryText, number, additionalText) =>
    discount > 0 && (
      <div className={classes.promotionalText}>
        <ORTypography className={classes.content_header} color="secondary">
          {primaryText}
        </ORTypography>
        <ORTypography className={classes.content_detail}>
          {number}
          {additionalText}
        </ORTypography>
      </div>
    );

  const promotionalText = promotionalTextBuilder(
    'Promotional Pricing Discount:\u00A0',
    discount,
    '%',
  );
  const discountText = promotionalTextBuilder(
    'Discount:\u00A0',
    `-$${discountCalculation}`,
    '\u00A0/Month',
  );

  // Calculate the price when region and plan are selected, recalculate when pricing factors change
  React.useEffect(() => {
    if (selectedRegion && selectedPlan.name) {
      // eslint-disable-next-line react/prop-types
      const region = regions.find((rg) => rg.name === selectedRegion);
      const planIndex = availablePlans.findIndex((ap) => ap.id === selectedPlan.id);
      const pricingAddons = selectedAddons.map((selectedAddon) => {
        const ao = availableAddons.find((availAddon) => availAddon.name === selectedAddon.name);
        return {
          id: ao.id,
          ...selectedAddon,
        };
      });
      getPrice(region.id, planIndex, availablePlans, pricingAddons);
    }
  }, [
    selectedRegion,
    selectedPlan,
    selectedAddons,
    regions,
    availableAddons,
    availablePlans,
    getPrice,
  ]);

  const addons = selectedAddons.map((addon) => (
    <ORTypography key={addon.name} className={classes.content_detail}>
      {addon.displayName}: {addon.value}
    </ORTypography>
  ));

  return (
    <div className={classes.drawerContainer}>
      <Drawer
        anchor="right"
        open={isOpen}
        variant="persistent"
        classes={{ paper: classes.drawerPaper }}
      >
        <div className="headerContainer">
          <ORTypography variant="h5" component="h1" className={classes.header} onClick={toggle}>
            <span className={classes.theX}>&times;</span>
            <span className={classes.monthlyCostEstimate}>Monthly Cost Estimate</span>
          </ORTypography>
        </div>

        <div className={classes.content}>
          <ORTypography className={classes.content_header} color="secondary">
            Service
          </ORTypography>
          <ORTypography className={classes.content_detail}>{selectedService}</ORTypography>
          {isServiceBeta && (
            <ORTypography
              className={classnames(classes.content_detail, classes.content_beta_warning)}
            >
              You have selected a BETA service and are agreeing to the{' '}
              <a
                target="_blank"
                rel="noopener noreferrer"
                className={classes.content_beta_warning_link}
                href="https://www.rackspace.com/information/legal/testterms"
              >
                Rackspace test terms
              </a>
              .
            </ORTypography>
          )}
          <ORTypography className={classes.content_header} color="secondary">
            Version
          </ORTypography>
          <ORTypography className={classes.content_detail}>{selectedVersion}</ORTypography>
          <ORTypography className={classes.content_header} color="secondary">
            Cloud Provider
          </ORTypography>
          <ORTypography className={classes.content_detail}>
            {selectedCloudProvider}
            {selectedCloudProviderIsBeta && <span className={classes.betaText}> BETA</span>}
          </ORTypography>
          {selectedCloudProviderIsBeta && (
            <ORTypography
              className={classnames(classes.content_detail, classes.content_beta_warning)}
            >
              You have selected a BETA service and are agreeing to the{' '}
              <a
                target="_blank"
                rel="noopener noreferrer"
                className={classes.content_beta_warning_link}
                href="https://www.rackspace.com/information/legal/testterms"
              >
                Rackspace test terms
              </a>
              .
            </ORTypography>
          )}
          <ORTypography className={classes.content_header} color="secondary">
            Region
          </ORTypography>
          <ORTypography className={classes.content_detail}>{selectedRegion}</ORTypography>
          <ORTypography className={classes.content_header} color="secondary">
            Flavor
          </ORTypography>
          <ORTypography className={classes.content_detail}>{flavorName}</ORTypography>
          {addons}
          <ORTypography className={classes.content_header} color="secondary">
            Storage / Memory
          </ORTypography>
          <ORTypography className={classes.content_detail}>{planInfoString}</ORTypography>
          <ORTypography className={classes.content_detail}>
            <em>{nodeInfoString}</em>
          </ORTypography>
          <ORTypography className={classes.content_header} color="secondary">
            Authorized Firewall IP
          </ORTypography>
          <ORTypography className={classes.content_detail}>{selectedCidr}</ORTypography>
          <ORTypography className={classes.content_header} color="secondary">
            Name
          </ORTypography>
          <ORTypography className={classes.content_detail}>{enteredName}</ORTypography>
          <div className={classes.billingContent}>
            <div className={classes.priceContainer}>
              <ORTypography variant="h5" component="h1" className={classes.priceText}>
                Total:&nbsp;
              </ORTypography>
              <Price />
              <ORTypography variant="h6" component="h1" className={classes.additionalPriceText}>
                &nbsp;/Month
              </ORTypography>
            </div>
            {isFastlaneCustomer && (
              <ORTypography className={classes.betaText}>
                This amount will be added to your Rackspace bill.
              </ORTypography>
            )}
            {promotionalText}
            {discountText}
            <div>
              <ORTypography className={classes.supportText}>
                Looking for enterprise-level solutions?
              </ORTypography>
              <ORTypography className={classes.supportText}>
                <a href="mailto:sales@objectrocket.com">Contact us for a custom quote today!</a>
              </ORTypography>
            </div>
          </div>
        </div>
      </Drawer>
    </div>
  );
}

UnstyledSummaryDrawer.propTypes = propTypes;
UnstyledSummaryDrawer.defaultProps = defaultProps;

export default UnstyledSummaryDrawer;
